// The freshest reading for one catalog signal (custom + conditional alarms,
// phase 5). Two sources describe the same path and neither is complete:
//
//   - the SIGNAL CATALOG (`GET /v1/boats/:boatId/signals`) carries the boat's
//     declared unit and the last value the cloud stored — always present, but a
//     snapshot taken when the page loaded;
//   - the LIVE WS FEED (`useBoatChrome().liveSamples`) carries values as they
//     arrive — current, but only for paths that have ticked since the socket
//     opened, and it can carry a structured payload the picker cannot print.
//
// This module picks between them by observation time and formats the winner, so
// the picker row, the "now 26.6 V" hint beside the set point, and the D8
// would-fire line all read the same number from the same rule.
//
// Units are VERBATIM STORE UNITS (plan D6) unless the caller asks otherwise. The
// catalog's declared unit wins over the sample's, because that is the unit the
// thresholds are authored in and the evaluator compares against — printing a
// live value in some other unit beside the set point would be a lie. This module
// resolves nothing about units on its own: a caller that has decided the reading
// belongs to an account category (ADR 0189) passes that decision in, and a caller
// that has not gets the store unit, byte for byte as before.

import type { UnitSettings } from '@neomaris/protocol';

import { formatThreshold } from './alarmSentence';
import type { UnitCategory } from './displayUnits';
import { isSwitchChannelPath, type Signal, type SignalValue } from './signalCatalog';

/** The shape this module needs from a `LiveSample` (`useBoatLiveData`). */
export interface LiveSampleLike {
  /** Whatever the boat reported: scalar, structured, or absent. */
  readonly value: unknown;
  /** The sample's unit as the boat labelled it — a fallback only. */
  readonly unit?: string;
  /** Epoch-ms the browser received the frame. */
  readonly receivedAt: number;
}

/** Where a rendered reading came from, so a caller can be honest about it. */
export type ReadingSource = 'live' | 'stored' | 'none';

/** One signal's current reading, already resolved between the two sources. */
export interface SignalReading {
  /** The scalar to show, or `null` when there is nothing showable. */
  readonly value: SignalValue;
  /** The unit to print beside it — the boat's declared store unit. */
  readonly unit: string | null;
  /** When this reading was observed, ISO, for freshness + relative wording. */
  readonly atIso: string | null;
  /** The same instant in epoch-ms, for staleness maths. */
  readonly atMs: number | null;
  readonly source: ReadingSource;
  /**
   * Render this reading as On/Off rather than as a number.
   *
   * Switch and indicator bank channels are flags that travel as 1/0 NUMBERS —
   * the Signal K mapper emits numbers, so the telemetry allowlist has to accept
   * them as numeric. Printed literally, a bilge-high-water channel reads "0",
   * which tells an operator nothing. The path is the only thing that knows, and
   * {@link signalReading} is the last place that still has it.
   */
  readonly onOff?: boolean;
}

function parseIso(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The showable scalar in a raw sample value. A structured payload (the position
 * object, say) and a non-finite number both become `null` — "arrived, nothing to
 * print" — which is also how the evaluator reads them (`isDataUnavailable`), so
 * the preview cannot disagree with the boat.
 *
 * Exported because the alarm detail page's live panel resolves a value from more
 * sources than this module ranks (a runtime snapshot's last-fired value among
 * them) and must flatten each one by the SAME rule.
 */
export function readingScalar(value: unknown): SignalValue {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value;
  return null;
}

/**
 * The reading to show for `signal`, preferring the live sample when it is at
 * least as recent as the stored one.
 *
 * The live frame wins on TIME, not on content: a fresh frame carrying a
 * structured payload still means "this path is reporting right now", so the
 * freshness dot goes green even though the value prints as nothing. Ranking the
 * other way would show a stale number under a green dot, which is the one
 * outcome worth avoiding.
 */
export function signalReading(signal: Signal, sample?: LiveSampleLike | null): SignalReading {
  const storedMs = parseIso(signal.lastValueAt);
  const onOff = isSwitchChannelPath(signal.path) ? { onOff: true } : {};
  const liveMs =
    sample !== undefined && sample !== null && Number.isFinite(sample.receivedAt)
      ? sample.receivedAt
      : null;

  if (sample !== undefined && sample !== null && liveMs !== null) {
    if (storedMs === null || liveMs >= storedMs) {
      return {
        value: readingScalar(sample.value),
        unit: signal.unit ?? sample.unit ?? null,
        atIso: new Date(liveMs).toISOString(),
        atMs: liveMs,
        source: 'live',
        ...onOff,
      };
    }
  }

  if (storedMs !== null || signal.lastValue !== null) {
    return {
      value: signal.lastValue,
      unit: signal.unit,
      atIso: signal.lastValueAt,
      atMs: storedMs,
      source: 'stored',
      ...onOff,
    };
  }

  // An accepted path the boat has never put a value on. Honest emptiness.
  return { value: null, unit: signal.unit, atIso: null, atMs: null, source: 'none', ...onOff };
}

/**
 * One scalar as text — "26.6 V", "On", "moored" — or `null` when there is
 * nothing to print. Numbers go through the sentence renderer's
 * {@link formatThreshold}, so the live hint and the review sentence format the
 * same value identically.
 *
 * `category` + `units` restate the number in the account's units (ADR 0189).
 * They belong to the CALLER: only a surface that knows what its own boxes are in
 * can say whether this reading should move with them. Omit either and nothing
 * converts, which is what every reader of a plain store-unit surface gets.
 */
export function formatValue(
  value: SignalValue,
  unit: string | null,
  category?: UnitCategory,
  units?: UnitSettings
): string | null {
  if (value === null) return null;
  if (typeof value === 'number') return formatThreshold(value, unit, category, units);
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  const text = value.trim();
  return text.length === 0 ? null : text;
}

/** {@link formatValue} for a resolved reading — its value in its declared unit
 *  (or the account's, when the caller names a category), or as On/Off when the
 *  reading is a switch/indicator channel's 1/0 flag. */
export function formatReading(
  reading: SignalReading,
  category?: UnitCategory,
  units?: UnitSettings
): string | null {
  if (reading.onOff === true && typeof reading.value === 'number') {
    return reading.value === 0 ? 'Off' : 'On';
  }
  return formatValue(reading.value, reading.unit, category, units);
}
